import { useEffect, useRef, useState, useCallback } from "react";
import { wsBaseUrl } from "./api";

// ============================================================
// useTranslationSocket — WebSocket lifecycle for the Live tab.
// ------------------------------------------------------------
// Features
//  * Auth via ?token=<jwt>
//  * Automatic reconnect every 3s with exponential cap at 10s
//  * Heartbeat ping every 20s
//  * Keeps a running phrase buffer (edits mutate in-place by phrase.id)
//  * Exposes helpers: sendPhrase, editPhrase, clearScreen, promote, demote, setRole
// ============================================================

const RECONNECT_DELAYS = [1500, 3000, 3000, 5000, 10000]; // ms
const HEARTBEAT_MS = 20000;

function uuid() {
  // RFC 4122 v4-ish — adequate for message correlation.
  return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function useTranslationSocket(sessionId, { enabled = true } = {}) {
  const [connected, setConnected] = useState(false);
  const [me, setMe] = useState(null); // welcome payload
  const [roster, setRoster] = useState([]);
  const [promotedSenders, setPromotedSenders] = useState([]);
  const [phrases, setPhrases] = useState([]); // ordered oldest -> newest
  const [lastError, setLastError] = useState(null);
  const [cleared, setCleared] = useState(0); // bump counter used by the UI

  const wsRef = useRef(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef(null);
  const heartbeatTimer = useRef(null);
  const closedByUs = useRef(false);

  const _send = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return false;
    ws.send(JSON.stringify(payload));
    return true;
  }, []);

  const connect = useCallback(() => {
    if (!enabled || !sessionId) return;
    const token = localStorage.getItem("kc_token") || localStorage.getItem("token") || "";
    const url = `${wsBaseUrl()}/api/ws/translation/${encodeURIComponent(sessionId)}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectAttempt.current = 0;
      // Start heartbeat
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = setInterval(() => _send({ type: "heartbeat" }), HEARTBEAT_MS);
    };

    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch (_) { return; }
      if (!msg || typeof msg !== "object") return;
      switch (msg.type) {
        case "welcome":
          setMe(msg);
          setRoster(msg.roster || []);
          setPromotedSenders(msg.promotedSenders || []);
          // Replay recent phrases (last 5) if we don't already have them.
          if (Array.isArray(msg.recentPhrases) && msg.recentPhrases.length > 0) {
            setPhrases((prev) => {
              const prevIds = new Set(prev.map((p) => p.id));
              const additions = msg.recentPhrases.filter((p) => !prevIds.has(p.id));
              return [...prev, ...additions];
            });
          }
          break;
        case "phrase":
          setPhrases((prev) => {
            if (prev.some((p) => p.id === msg.id)) return prev;
            return [...prev, msg];
          });
          break;
        case "edit":
          setPhrases((prev) => prev.map((p) =>
            p.id === msg.phraseId
              ? { ...p, text: msg.text, translation: msg.translation, edited: true, editedAt: msg.timestamp }
              : p
          ));
          break;
        case "clear":
          setPhrases([]);
          setCleared((n) => n + 1);
          break;
        case "presence":
          setRoster(msg.roster || []);
          // Only react to presence role_change if it was for THIS connection
          // (the backend echoes connection_id for role_change events).
          if (msg.event === "role_change" && me && msg.connectionId && msg.connectionId === me.yourConnectionId) {
            setMe((prev) => prev ? { ...prev, yourRole: msg.role } : prev);
          }
          break;
        case "promotion":
          setPromotedSenders(msg.promotedSenders || []);
          setRoster(msg.roster || []);
          // Promotion/demotion affects all my connections for that user.
          if (me && msg.userId === me.yourUserId) {
            if (msg.promoted) {
              // We can now be sender in any tab. Don't flip existing role
              // automatically — let the user choose via Switch Role.
              setMe((prev) => prev ? { ...prev, canPromoteToSender: true } : prev);
            }
          }
          break;
        case "error":
          setLastError(msg.detail || "Unknown error");
          break;
        case "heartbeat_ack":
          // noop
          break;
        default:
          // ignore
          break;
      }
    };

    ws.onerror = () => {
      // Let onclose drive reconnect logic.
    };

    ws.onclose = () => {
      setConnected(false);
      if (heartbeatTimer.current) { clearInterval(heartbeatTimer.current); heartbeatTimer.current = null; }
      if (closedByUs.current) return;
      // Reconnect
      const delay = RECONNECT_DELAYS[Math.min(reconnectAttempt.current, RECONNECT_DELAYS.length - 1)];
      reconnectAttempt.current += 1;
      reconnectTimer.current = setTimeout(() => connect(), delay);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionId, _send]);

  useEffect(() => {
    closedByUs.current = false;
    if (enabled && sessionId) connect();
    return () => {
      closedByUs.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) {}
        wsRef.current = null;
      }
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionId]);

  // ---- Imperative helpers ----
  const sendPhrase = useCallback((text, translation) => {
    const id = uuid();
    const ok = _send({ type: "phrase", id, text, translation, timestamp: new Date().toISOString() });
    if (!ok) return null;
    // Optimistic — the server will echo back and dedupe by id.
    return id;
  }, [_send]);

  const editPhrase = useCallback((phraseId, text, translation) => {
    return _send({ type: "edit", phraseId, text, translation });
  }, [_send]);

  const clearScreen = useCallback(() => _send({ type: "clear" }), [_send]);

  const promote = useCallback((userId) => _send({ type: "promote", userId }), [_send]);
  const demote = useCallback((userId) => _send({ type: "demote", userId }), [_send]);
  const setRole = useCallback((role) => _send({ type: "role_change", role }), [_send]);

  return {
    connected,
    me,
    roster,
    promotedSenders,
    phrases,
    lastError,
    cleared,
    sendPhrase,
    editPhrase,
    clearScreen,
    promote,
    demote,
    setRole,
  };
}
